import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import WorkerLayout from '../../layouts/WorkerLayout';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import SecuritySection from '../../components/common/SecuritySection';
import ProfileCompletionModal from '../../components/common/ProfileCompletionModal';
import { useAuthStore } from '../../store/authStore';

const LOCK_JOBS = [
  { title: 'Construction Site Helper', company: 'R.K. Builders', location: 'Mumbai', wage: '650' },
  { title: 'Welding Work – Factory', company: 'Steel India Pvt Ltd', location: 'Pune', wage: '800' },
  { title: 'Painting & Finishing', company: 'Ravi Contractors', location: 'Nashik', wage: '550' },
  { title: 'Carpenter – Interior', company: 'HomeCraft Interiors', location: 'Surat', wage: '700' },
  { title: 'Security Guard – Night', company: 'SecureForce Ltd', location: 'Ahmedabad', wage: '500' },
];

export default function WorkerDashboard() {
  const navigate = useNavigate();
  const user     = useAuthStore((s) => s.user);
  const [profile,    setProfile]    = useState(null);
  const [matches,    setMatches]    = useState([]);
  const [notifs,     setNotifs]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [userInfo,   setUserInfo]   = useState(null);
  const [showModal,  setShowModal]  = useState(false);

  useEffect(() => {
    Promise.allSettled([
      api.get('/worker/profile'),
      api.get('/worker/matches?limit=5'),
      api.get('/notifications?limit=5'),
      api.get('/auth/me'),
    ]).then(([p, m, n, u]) => {
      if (p.status === 'fulfilled') setProfile(p.value.data.data);
      if (m.status === 'fulfilled') setMatches(m.value.data.data || []);
      if (n.status === 'fulfilled') setNotifs(n.value.data.data  || []);
      if (u.status === 'fulfilled') {
        const d = u.value.data.data;
        setUserInfo(d);
        if (!d.first_name || !d.last_name || !d.email || !d.has_password) {
          setShowModal(true);
        }
      }
      setLoading(false);
    });
  }, []);

  function handleModalSaved() {
    setShowModal(false);
    api.get('/auth/me').then((r) => setUserInfo(r.data.data)).catch(() => {});
  }

  if (loading) {
    return (
      <WorkerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full" />
        </div>
      </WorkerLayout>
    );
  }

  const firstName      = profile?.full_name?.split(' ')[0] || user?.mobile;
  const isExpired      = profile?.expiry_date && new Date(profile.expiry_date) < new Date();
  const isUnlocked     = profile?.expiry_date && !isExpired;
  const needsProfile   = !profile;
  const needsPayment   = profile && !profile.expiry_date;

  return (
    <WorkerLayout>
      <div className="space-y-5 pb-20 sm:pb-6">

        {/* ── Welcome Banner ── */}
        <div className="bg-gradient-to-r from-brand-800 to-brand-600 rounded-2xl p-5 text-white">
          <h2 className="text-xl font-bold">नमस्ते, {firstName}! 👋</h2>
          <p className="text-brand-200 text-xs mt-0.5">{user?.mobile}</p>
          {profile && (
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge label={profile.verification_status} variant={profile.verification_status} />
              <Badge label={profile.availability_status} variant={profile.availability_status} />
            </div>
          )}
        </div>

        {/* ── Alerts ── */}
        {needsProfile && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <span className="text-2xl">⚠️</span>
            <div className="flex-1">
              <p className="font-semibold text-amber-900">Create your profile to get started</p>
              <p className="text-sm text-amber-700 mt-1">
                Build your profile and pay <strong>₹10 Validation &amp; Verification fee</strong> to appear to employers and unlock all jobs.
              </p>
              <Link to="/worker/profile/create">
                <Button size="sm" className="mt-2">Create Profile →</Button>
              </Link>
            </div>
          </div>
        )}

        {needsPayment && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <span className="text-2xl">🔓</span>
            <div className="flex-1">
              <p className="font-semibold text-blue-900">Pay ₹10 to activate your account</p>
              <p className="text-sm text-blue-700 mt-1">
                One-time <strong>₹10 Validation &amp; Verification fee</strong> unlocks all jobs, matches, and your Digital Worker Card. Valid for 1 year.
              </p>
              <Button size="sm" className="mt-2" onClick={() => navigate('/worker/payment/registration')}>
                Pay ₹10 Now →
              </Button>
            </div>
          </div>
        )}

        {isExpired && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <span className="text-2xl">⛔</span>
            <div className="flex-1">
              <p className="font-semibold text-red-800">Registration Expired</p>
              <p className="text-sm text-red-700 mt-1">Renew your <strong>₹10 Validation &amp; Verification fee</strong> to stay visible to employers.</p>
              <Link to="/worker/payment/renew">
                <Button size="sm" className="mt-2">Renew Now ₹10</Button>
              </Link>
            </div>
          </div>
        )}

        {/* ── Quick Stats (only when active) ── */}
        {isUnlocked && (
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <p className="text-2xl font-bold text-brand-600">{profile.profile_complete_pct ?? 0}%</p>
              <p className="text-xs text-gray-500 mt-1">Profile Complete</p>
              {(profile.profile_complete_pct ?? 0) < 80 && (
                <Link to="/worker/profile/create" className="text-xs text-brand-600 hover:underline">Complete it →</Link>
              )}
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{matches.length}</p>
              <p className="text-xs text-gray-500 mt-1">Job Matches</p>
              <Link to="/worker/matches" className="text-xs text-brand-600 hover:underline">View all →</Link>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4 text-center col-span-2">
              <p className="text-xs font-medium text-gray-500">Validation Valid Until</p>
              <p className="text-lg font-bold text-gray-900 mt-0.5">
                {new Date(profile.expiry_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
        )}

        {/* ── Digital Worker Card CTA ── */}
        {isUnlocked && (
          <Link to="/worker/card"
            className="flex items-center gap-4 bg-gradient-to-r from-brand-700 to-brand-500 rounded-xl p-4 text-white hover:from-brand-800 hover:to-brand-600 transition-all">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">🪪</div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm">Your Digital Worker Card</p>
              <p className="text-brand-200 text-xs mt-0.5">View, share &amp; download your verified ID card</p>
            </div>
            <span className="text-white/70 text-lg flex-shrink-0">›</span>
          </Link>
        )}

        {/* ── Jobs Section (locked / unlocked) ── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Available Jobs</h3>
            {isUnlocked && (
              <Link to="/worker/jobs" className="text-sm text-brand-600 hover:underline">View all →</Link>
            )}
          </div>

          {isUnlocked ? (
            /* Unlocked: show real matches */
            matches.length > 0 ? (
              <div className="space-y-3">
                {matches.map((m) => (
                  <Link key={m.requirement_id} to={`/worker/jobs/${m.requirement_id}`}
                    className="block bg-white rounded-xl border border-gray-200 p-4 hover:border-brand-300 transition-colors">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{m.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{m.company_name} · {m.location_text}</p>
                      </div>
                      <div className="text-right ml-2 flex-shrink-0">
                        <span className="bg-brand-50 text-brand-700 text-xs font-bold px-2 py-1 rounded-full">{m.match_score}% match</span>
                        {m.wage_min && <p className="text-xs text-gray-500 mt-1">₹{m.wage_min}/day</p>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">
                <p className="text-2xl mb-2">🔍</p>
                <p className="text-sm">No matches yet. Complete your profile to get matched.</p>
              </div>
            )
          ) : (
            /* Locked: show blur preview */
            <div className="relative rounded-xl overflow-hidden">
              <div className="space-y-3 select-none pointer-events-none">
                {LOCK_JOBS.map((j, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 blur-sm">
                    <p className="font-medium text-gray-900 text-sm">{j.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{j.company} · {j.location}</p>
                    <p className="text-xs text-gray-500 mt-1">₹{j.wage}/day</p>
                  </div>
                ))}
              </div>
              {/* Lock overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 backdrop-blur-[2px]">
                <div className="bg-white rounded-2xl shadow-xl p-6 mx-4 text-center max-w-xs">
                  <div className="w-14 h-14 bg-brand-50 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-3xl">🔒</span>
                  </div>
                  <p className="font-bold text-gray-900">Jobs are locked</p>
                  <p className="text-sm text-gray-500 mt-1 mb-4">
                    {needsProfile
                      ? 'Create your profile and pay ₹10 to see all available jobs'
                      : 'Pay ₹10 Validation & Verification fee to unlock jobs'}
                  </p>
                  <Button fullWidth onClick={() => navigate(needsProfile ? '/worker/profile/create' : '/worker/payment/registration')}>
                    {needsProfile ? 'Create Profile' : 'Pay ₹10 to Unlock'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Notifications ── */}
        {isUnlocked && notifs.length > 0 && (
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Recent Notifications</h3>
            <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
              {notifs.map((n) => (
                <div key={n.id} className={`p-4 ${!n.read_at ? 'bg-brand-50' : ''}`}>
                  <p className="text-sm font-medium text-gray-900">{n.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{n.body}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Login & Security ── */}
        <SecuritySection readOnlyEmail />
      </div>

      {showModal && userInfo && (
        <ProfileCompletionModal
          userData={userInfo}
          onClose={() => setShowModal(false)}
          onSaved={handleModalSaved}
        />
      )}
    </WorkerLayout>
  );
}
